import { abLegStops, escapeAttr, favouriteStatus, getEmissionText, getFlightEmissionsData, getTripType, insertIf, isSavedAsFavorite, join, moreInfoAgencyCount, passengersText, renderIndex, ticketTypeIcon, tripGetToAndFromCity } from '@js/components/resultsRenderHelpers';
import { capitalize, deepCopy, getAgencyImagePath, getTrackingClass, humanJoin, l, lc, linkPrefix, lreplace, setUrlParameter } from '@utils/toolbox';
import { resultsRender } from './resultsRender';
import { Civet } from '@js/plugins/civet';
import isFlightAboveEconomy, { getTicketClassText } from '@js/utils/flight';

/**
 * Renders a list of journeys
 * @param {Array} journeys - The list of journeys to render
 * @param {Object} options - Options for rendering
 * @param {boolean} options.showEmissions - Whether to show emissions
 * @param {boolean} options.isFavoritePage - Whether the page is a favorite page
 * @param {boolean} options.displayFavBtn - Whether to display the favorite button
 * @param {number} options.passengers - Number of passengers
 * @param {boolean} options.multiplePassengers - Whether there are multiple passengers
 * @param {boolean} options.preview - Whether to show a preview
 * @param {Object} options.filters - Filters for rendering
 * @param {boolean} options.filters.flexTicket - Whether to show flex ticket
 * @param {boolean} options.filters.baggageIncluded - Whether to show baggage included
 * @param {boolean} options.filters.cabinBaggageIncluded - Whether to show cabin baggage included
 * @param {boolean} options.filters.showEmissions - Whether to show emissions
 * @param {boolean} options.filters.isSortedByNet - Whether to show sorted by net
 * @param {boolean} options.filters.isEmissionAbnormallyHigh - Whether to show emission abnormally high
 *
*/
/**
 * Renders a list of journeys.
 * @param {Array} journeys - The list of journeys to render.
 * @param {Object} options - Options for rendering.
 * @returns {string} - HTML string of rendered journeys.
 */
export function renderJourneys (journeys, options, source) {
	return journeys.flatMap((journey, index) => {
		const rendered = [renderJourney(journey, options, source)];

		return rendered;
	}).join('');
}

const PRODUCTS = {
	BAGGAGE: 'baggage',
	REBOOKABLE: 'rebookable',
	CABIN_BAGGAGE: 'cabinBaggage',
	CARBON_OFFSET: 'carbonOffset',
	UPGRADE_PREMIUM_ECONOMY: 'upgradePremiumEconomy',
	UPGRADE_BUSINESS: 'upgradeBusiness',
	UPGRADE_FIRST_CLASS: 'upgradeFirstClass'
};

const UPGRADE_LABELS = [
	PRODUCTS.UPGRADE_PREMIUM_ECONOMY,
	PRODUCTS.UPGRADE_BUSINESS,
	PRODUCTS.UPGRADE_FIRST_CLASS
];

const PRODUCT_LABELS = {
	[PRODUCTS.REBOOKABLE]: {
		key: 'global.simplePhrases.rebooking',
		tooltip: 'global.simplePhrases.flexTicket',
		frontKey: 'flexTicket'
	},
	[PRODUCTS.BAGGAGE]: {
		key: 'global.simplePhrases.baggageIncluded',
		tooltip: 'global.simplePhrases.baggageRestriction',
		frontKey: 'baggageIncluded'
	},
	[PRODUCTS.CABIN_BAGGAGE]: {
		key: 'global.simplePhrases.cabinBaggageIncluded',
		tooltip: 'global.simplePhrases.cabinBaggageRestriction',
		frontKey: 'cabinBaggageIncluded'
	},
	[PRODUCTS.UPGRADE_PREMIUM_ECONOMY]: {
		key: 'searchBox.ticketType.premium',
		tooltip: 'showresult.promotedProduct.premiumEconomy',
		frontKey: 'upgradePremiumEconomy'
	},
	[PRODUCTS.UPGRADE_BUSINESS]: {
		key: 'searchBox.ticketType.business',
		tooltip: 'showresult.promotedProduct.business',
		frontKey: 'upgradeBusiness'
	},
	[PRODUCTS.UPGRADE_FIRST_CLASS]: {
		key: 'searchBox.ticketType.firstClass',
		tooltip: 'showresult.promotedProduct.firstClass',
		frontKey: 'upgradeFirstClass'
	}
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Computes CSS classes for a journey result.
 * @param {Object} journey - Journey object.
 * @param {Object} options - Rendering options.
 * @returns {string} - CSS class string.
 */
function getResultClasses (journey, options) {
	return [
		'abResult',
		`abResult--${getTripType(journey)}`,
		favouriteStatus(journey),
		options.preview && 'abResult--preview',
		journey.bestValue && `abResult--${journey.bestValue}Best`,
		journey.shared && 'shared-result'
	].filter(Boolean).join(' ');
}

/**
 * Renders content for favorite status.
 * @param {Object} favouriteMeta - Favorite metadata.
 * @returns {string} - HTML string for favorite status.
 */
function getFavouriteStatusContent (favouriteMeta) {
	if (favouriteMeta?.isPast) {
		return `<div class="pastResult--content" data-action="dismiss">
			<span>${l('fav.ui.isOldRecord')}</span>
		</div>`;
	}
	if (favouriteMeta?.isOld) {
		return `<div class="oldResult--content" data-action="refresh">
			<span>${l('fav.ui.needsRefresh')}<span class="icon-loading"></span></span>
		</div>`;
	}
	return '';
}

// ============================================================================
// RENDERING FUNCTIONS
// ============================================================================

/**
 * Renders agent meta information.
 * @param {Object} journey - Journey object.
 * @param {boolean} isFirst - Is first agent.
 * @param {boolean} hasPromotedAgent - Has promoted agent.
 * @returns {string} - HTML string for agent meta.
 */
function renderAgentMeta (journey, isFirst, hasPromotedAgent) {
	const agentCount = journey.agents.length;
	const onlyTwoAgents = agentCount === 2;

	if (agentCount === 1) {
		return '<div class="abResult__viewAllAgencies" data-action="view-all-agencies"></div>';
	}

	if ((isFirst || onlyTwoAgents) && hasPromotedAgent) {
		const index = isFirst ? 0 : 1;
		return renderOtaName(journey.agents[index].agent);
	}

	return `<div class="abResult__viewAllAgencies" data-action="view-all-agencies">
		${renderResultAgentPreview(journey.agents, 0, 2)}
		${renderResultAgentShowAll(journey.agents)}
	</div>`;
}

/**
 * Renders OTA name block.
 * @param {string} agentName - Agent name eg: sas NOT se_sas.
 * @returns {string} - HTML string for OTA name.
 */
function renderOtaName (agentName) {
	const fullAgentName = js_params?.module?.Filters?.agencies?.[agentName] || agentName;
	return `
		<div class="abResult__viewAllAgencies">
			<div class="abResult__additionalAgencyText abResult__additionalAgencyText--variation">
				${l('showresult.result.agenciesAvailableAt')}: <span>${fullAgentName}</span>
			</div>
		</div>`;
}


/**
 * Renders agent product label.
 * @param {string} product - Product key.
 * @returns {string} - HTML string for product label.
 */
function renderAgentProduct (agent) {
	const promotedProduct = agent?.promotedProduct;
	const filteredProduct = getActiveProduct();

	// If no promoted product
	if (!promotedProduct) {
		return '';
	}


	const config = PRODUCT_LABELS[promotedProduct];
	if (!config?.key) {
		Civet.reportError(new Error(`No label config found for product: ${promotedProduct}`));
		return '';
	}

	// Only show and if there is an active filter and it's not a cabin upgrade
	const labelText = filteredProduct && !UPGRADE_LABELS.includes(promotedProduct)
		? `<span class="abBuyBtn__bestLabelContent--label" style="text-transform:lowercase">${l('global.simplePhrases.and')} ${l(config.key)}</span>`
		: `<span class="abBuyBtn__bestLabelContent--label">${l(config.key)}</span>`;

	const tooltipText = getTooltip(promotedProduct);

	return `
<div class="abBuyBtn__bestLabel abBuyBtn__bestLabel--${config.frontKey}">
	<div class="abBuyBtn__bestLabelContent abBuyBtn__bestLabelContent--${config.frontKey}" data-tooltip="${tooltipText}">
		<span class="icon icon-info"></span>&nbsp;
		${labelText}
	</div>
</div>`;
}

/**
 * Determines the active product filter.
 * @returns {string|null} - The active product key or null.
 */
function getActiveProduct () {
	if (resultsRender.isBaggageIncludedFiltered) {
		return PRODUCTS.BAGGAGE;
	}
	if (resultsRender.isFlexTicketFiltered) {
		return PRODUCTS.REBOOKABLE;
	}
	if (resultsRender.isCabinBaggageIncludedFiltered) {
		return PRODUCTS.CABIN_BAGGAGE;
	}
	return null;
}

function getTooltip (product) {
	const productChecks = [
		{ condition: resultsRender.isBaggageIncludedFiltered, label: PRODUCT_LABELS[PRODUCTS.BAGGAGE].tooltip },
		{ condition: resultsRender.isCabinBaggageIncludedFiltered, label: PRODUCT_LABELS[PRODUCTS.CABIN_BAGGAGE].tooltip },
		{ condition: resultsRender.isFlexTicketFiltered, label: PRODUCT_LABELS[PRODUCTS.REBOOKABLE].tooltip },
		{ condition: product, label: PRODUCT_LABELS[product]?.tooltip }
	];

	const tooltips = productChecks.filter(p => p.condition).map(p => l(p.label).toLowerCase());

	// If upgrade product, show only that tooltip
	if (UPGRADE_LABELS.includes(product)) {
		return l(PRODUCT_LABELS[product].tooltip);
	}

	return lreplace('showresult.promoted.multi.tooltip', { products: humanJoin(tooltips) });
}

/**
 * Renders agent label HTML block.
 * @param {Object} config - Label config object.
 * @param {string} labelText - Label text.
 * @returns {string} - HTML string for label.
 */
function renderAgentLabelHtml (config, labelText, tooltipText) {
	return `
		<div class="abBuyBtn__bestLabel abBuyBtn__bestLabel--${config.frontKey}" data-tooltip="${tooltipText}">
			<div class="abBuyBtn__labelTooltip">
				<span class="icon icon-info" data-tooltip="${tooltipText}"></span>
			</div>
			<div class="abBuyBtn__bestLabelContent abBuyBtn__bestLabelContent--${config.frontKey}">
				${labelText}
			</div>
		</div>`;
}

/**
 * Renders OTA anchor (buy button).
 * @param {number} resultIndex - Result index.
 * @param {Object} agent - Agent object.
 * @param {number} agentIndex - Agent index.
 * @param {string} source - Source string.
 * @param {string} product - Product key.
 * @returns {string} - HTML string for anchor.
 */
function renderOtaAnchor (resultIndex, agent, agentIndex, source, product) {
	const isPromotedAgent = agent.promotedPrice !== undefined;
	let { code, bookingUrl, price } = agent;
	const buttonClasses = getAnchorTrackingClasses(resultIndex, agent, agentIndex, source, product, isPromotedAgent);

	// Add product to url
	bookingUrl = getBookingUrl(bookingUrl, source);

	return `
		<a href="${bookingUrl}" 
			class="${buttonClasses}" 
			target="_blank" 
			rel="nofollow noopener" 
			aria-label="${agent.agent}: ${lc(price)}" 
			tabindex="-1">
			<div class="abBuyBtn abBuyBtn--main">
				${renderAgentProduct(agent)}
				<div class="abBuyBtn__previewAgency">
					<img src="${getAgencyImagePath({ code })}"
						data-agent="${code}"
						class="agency-image"
						width="90"
						height="25"
						alt="${code}">
				</div>
				<div class="abBuyBtn__previewPrice">
					<span class="abBuyBtn__previewPrice--text">${l('showresult.result.goToTrip')}</span>
					<span>${lc(price)}</span>
				</div>
			</div>
		</a>`;
}

/**
 * Generates booking URL with parameters.
 * @param {string} bookingUrl - Base booking URL.
 * @param {string} source - Source string.
 * @returns {string} - Booking URL with parameters.
 */
function getBookingUrl (bookingUrl, source) {
	if (!bookingUrl) {
		return;
	}
	const params = {};

	if (resultsRender.isYouthSearch && resultsRender.isYouthFiltered) {
		params.youthTicket = 1;
	}

	if (js_params.controllerClass === 'page\\favorite') {
		params.favourite = 1;
	}

	params.source = source;

	for (const [key, value] of Object.entries(params)) {
		bookingUrl = setUrlParameter(bookingUrl, key, value);
	}

	if (linkPrefix.base) {
		bookingUrl = linkPrefix.base + bookingUrl;
	}

	return bookingUrl;
}


/**
 * Renders OTA preview block.
 * @param {Object} journey - Journey object.
 * @param {Object} agent - Agent object.
 * @param {number} agentIndex - Agent index.
 * @param {string} product - Product key.
 * @param {string} source - Source string.
 * @param {boolean} isFirst - Is first agent.
 * @param {boolean} hasPromotedAgent - Has promoted agent.
 * @returns {string} - HTML string for preview.
 */
function renderOtaPreview (journey, agent, agentIndex, product, source, isFirst, hasPromotedAgent) {
	return `
		<div class="abResult__otaAndBuyPreview">
			${renderAgentMeta(journey, isFirst, hasPromotedAgent) || ''}
			${renderOtaAnchor(journey.resultIndex, agent, agentIndex, source, product)}
		</div>`;
}

// ============================================================================
// MAIN RENDERING FUNCTIONS
// ============================================================================

/**
 * Renders a single journey result.
 * @param {Object} journey - Journey object.
 * @param {Object} options - Rendering options.
 * @returns {string} - HTML string for journey.
 */
function renderJourney (journey, options, source = null) {
	journey = deepCopy(journey);
	source = source ?? window?.showresult?.activeView ?? 'list';

	const baseClasses = getResultClasses(journey, options);
	const resultIndex = journey.resultIndex;
	const agents = journey.agents || [];
	const promotedAgent = journey.agents.find(agent => agent.promotedPrice !== undefined);
	const promotedProduct = promotedAgent ? promotedAgent.promotedProduct : undefined;
	const hasPromotedAgent = Boolean(promotedProduct);

	return `
		<div class="${baseClasses}"
			data-index="${resultIndex}"
			${journey.favouriteMeta?.isChanging ? 'data-status="removing"' : ''}
			tabindex="0"
			aria-label="${escapeAttr(journey.ariaLabel)}">
			
			${options.showEmissions ? renderEmissionBlock(journey) : ''}
			
			<div class="abResult__main" data-action="toggle-trip-details">
				${renderMainHeader(journey, options, resultIndex)}
				<div class="abResult__legs abResult__legs--default">
					${join(journey.trips, t => renderTrip(journey, t))}
				</div>
				${renderSummaries(journey, options)}
				<div class="abResult__flightDetails"></div>
			</div>

			${renderOtaPreview(journey, agents[0], 0, promotedProduct, source, true, hasPromotedAgent)}
			${insertIf((hasPromotedAgent && agents?.[1]), () => renderOtaPreview(journey, agents[1], 1, promotedProduct, source, false, hasPromotedAgent))}

			<div class="abResult__otaAndBuyAll"></div>
			${getFavouriteStatusContent(journey.favouriteMeta)}
		</div>`;
}

/**
 * Renders agent view for modal or expanded view.
 * @param {Object} journey - Journey object.
 * @param {string} source - Source string.
 * @param {boolean} isModal - Is modal view.
 * @param {number} resultIndex - Result index.
 * @returns {string} - HTML string for agent view.
 */
export function renderResultAgentsViewMore (journey, source, isModal) {
	journey = deepCopy(journey);

	const agents = journey.agents || [];
	const promotedAgent = agents.find(agent => agent.promotedProduct !== undefined);
	const promotedProduct = promotedAgent ? promotedAgent.promotedProduct : undefined;
	const hasPromotedAgent = Boolean(promotedProduct);

	let offset = hasPromotedAgent ? 2 : 1;
	if (isModal) {
		offset = 0;
	}

	return join(agents.slice(offset)
		, (agentData, index) => `
		<div class="abResult__otaAndBuyPreview">
			${renderOtaName(agentData.agent)}
			${renderOtaAnchor(journey.resultIndex, agentData, index + offset, source, promotedProduct, hasPromotedAgent, false)}
		</div>`);
}


/**
 * Renders emission block for a journey.
 * @param {Object} journey - Journey object.
 * @returns {string} - HTML string for emission block.
 */
function renderEmissionBlock (journey) {
	if (!journey) {
		return '';
	}

	const {
		isFullyCompensated,
		isEmissionAbnormallyHigh,
		hasAirlineCompensation,
		airlineCompensation,
		userCompensation,
		emissionLegend,
		emissionValueWithUnit,
		emissionDefinition,
		isSortedByNet,
		emissionValue
	} = getFlightEmissionsData(journey.raw_emission, journey.compensated_emission, journey.emission);


	const generateUserCompensationClasses = () => {
		const classes = ['compensationDistribution__arrow', 'compensationDistribution__arrow--user'];
		if (!isFullyCompensated) {
			classes.push('compensationDistribution__arrow--visible');
		}
		if (!hasAirlineCompensation) {
			classes.push('compensationDistribution__arrow--unique');
		}
		return classes.join(' ');
	};

	return `
<div class="abResult__emission" data-emission="${emissionValue}">
	<div class="emission__header emission__row">
		<div class="emission__col emission__col--climateCompensation">
		<span class="emission__title">${getEmissionText(journey) ?? ''}</span>
		</div>
	</div>

	<div class="emission__main emission__row ${insertIf(isEmissionAbnormallyHigh, 'emission--error')}">
		<div class="emission__col emission__col--carbonFootprint">
			<img src="${linkPrefix.assetPrefix}/images/global/CO2-cloud.png" alt="co2 cloud logo">
			<span>${emissionValueWithUnit}${insertIf(isSortedByNet, '*')}</span>
		</div>
		<div class="emission__col emission__col--climateCompensation compensationDistribution${insertIf(!isFullyCompensated, ' compensation-user')}" data-airline="${airlineCompensation}" data-user="${userCompensation}">
		<div class="compensationDistribution__arrow compensationDistribution__arrow--airline${insertIf(hasAirlineCompensation, ' compensationDistribution__arrow--visible')}" style="width:${airlineCompensation}%"></div>
		<div class="${generateUserCompensationClasses()}" ${insertIf(!isFullyCompensated, () => `style="width:${userCompensation}%"`)}></div>

		<div class="compensationDistribution__legend">
			${emissionLegend ?? ''}
		</div>
		</div>
	</div>

	${insertIf(isSortedByNet, () => `<div class="emission__footer emission__row">${emissionDefinition ?? ''}</div>`)}
</div>`;
}

/**
 * Renders main header for a journey result.
 * @param {Object} journey - Journey object.
 * @param {Object} options - Rendering options.
 * @param {number} index - Result index.
 * @returns {string} - HTML string for main header.
 */
function renderMainHeader (journey, options, index) {
	/* variant for favourite page vs normal search */
	if (options.isFavoritePage) {
		return `
<div class="abResult__airlineAndMoreInfo">
	<span class="tripIndex">${renderIndex(index)}</span>
	${renderFavouriteHeader(journey)}
	<div class="tools">
		<span class="icon-info" data-action="info"></span>
		<span class="icon-delete" data-action="delete"></span>
		<span class="icon icon-heart-full" data-action="delete"></span>
	</div>
</div>`;
	}

	return `
<div class="abResult__airlineAndMoreInfo">
	<span>${journey.airlines}</span>
	${journey.agents.length
		? `<span>${moreInfoAgencyCount(journey.agents.length)}</span>`
		: `<span>${l('showresult.result.showMoreInfo')}</span>`
	}
	${insertIf(options.displayShareButton, () => `
	<div class="abResult__shareBtn--container">
		<div class="abResult__shareBtn abBtn abBtn--flex" data-result="${index}">
			<span>${l('global.simplePhrases.share')}</span>
			<span class="icon icon-share-2 icon-with-tooltip" data-tooltip="${l('global.simplePhrases.share')}"></span>
		</div>
	</div>
	`)}

	<div class="abResult__flightDetailsBtn">
		<span>${l('showresult.result.showMoreInfo')}</span>
	</div>

${insertIf(options.displayFavBtn, () => `
	<div class="abResult__flightFavBtn ${isSavedAsFavorite(journey)}">
		<span class="icon icon-heart-empty"></span>
		<span class="icon icon-heart-full"></span>
	</div>`)}
</div>
`;
}

/**
 * Renders favorite header for a journey.
 * @param {Object} journey - Journey object.
 * @returns {string} - HTML string for favorite header.
 */
function renderFavouriteHeader (journey) {
	let left = `<div class="tripDestinations">`;
	let right = `<div class="tripDates">`;

	const tripType = getTripType(journey);
	const { fromCity: departureFromCity, toCity: departureToCity } = tripGetToAndFromCity(journey.trips[0]);

	if (tripType === 'return') {
		const { fromCity: returnFromCity } = tripGetToAndFromCity(journey.trips[journey.trips.length - 1]);
		left += `<span class="tripDestination">${departureFromCity}<span class='icon-switch'></span>${returnFromCity}</span>`;
	} else if (tripType === 'oneway') {
		left += `<span class="tripDestination">${departureFromCity}<span class='icon-arrow-right'></span>${departureToCity}</span>`;
	} else if (tripType === 'openjaw') {
		for (const trip of journey.trips) {
			const { fromCity, toCity } = tripGetToAndFromCity(trip);
			left += `<span class="tripDestination">${fromCity}<span class='icon-arrow-right'></span>${toCity}</span>`;
		}
	}
	right += `<span class="tripDate"><span class="tripDate--title">${l('global.simplePhrases.depart')}</span> ${journey.trips[0].segment[0].localizedArrivalDayShortString}</span>`;
	right += `<span class="tripDate"><span class="tripDate--title">${(tripType === 'oneway') ? l('global.simplePhrases.arrival') : l('global.simplePhrases.return')}</span> ${journey.trips[journey.trips.length - 1].segment[journey.trips[journey.trips.length - 1].segment.length - 1].localizedArrivalDayShortString}</span>`;

	return left + `</div>` + right + `</div>`;
}


/**
 * Renders a trip leg.
 * @param {Object} trip - Trip object.
 * @returns {string} - HTML string for trip leg.
 */
function renderTrip (journey, trip) {
	return `
<div class="abResult__leg">
	${join(trip?.segment, (segment, index, originalArray) => renderSegment(journey, segment, index, originalArray.length, trip))}
</div>`;
}

/**
 * Renders a segment of a trip.
 * @param {Object} segment - Segment object.
 * @param {number} index - Segment index.
 * @param {number} length - Total segments.
 * @param {Object} trip - Trip object.
 * @returns {string} - HTML string for segment.
 */
function renderSegment (journey, segment, index, length, trip) {
	const first = index === 0;
	const last = index === length - 1;
	let html = '';

	/* first-segment: airline logo + depart time */
	if (first) {
		html += `
		<div class="abResult__icao">
		  <img src="${linkPrefix.assetPrefix}/images/airlines/iata/small/${trip.airlineCode}.png"
				class="abResult__airlineImg"
				title="${trip.airline}"
				alt="${trip.airline}"
			>
		</div>
		<div class="abResult__depart"><span>${segment.departureTime}</span></div>
  
		<div class="abResult__iatas${journey.mixedCabinType ? ' combined-cabin' : ''}">
		  <div class="abIataTimespan ${segment.requiresAirportChange ? 'abIataTimespan--red' : `abIataTimespan--${trip.waitingTimeStatus}`}">
			<div class="abIataTimespan__iata depart">
			  <span>${segment.departureAirport}</span>
			</div>
  
			<div class="abIataTimespan__time">
			  <div class="abIataTimespan__time--decoration-up">
				<span>${trip.totalTravelTime}</span>
			  </div>
			  ${join(trip?.waiting?.airports, airport => `<div class="abIataTimespan__dot" style="left:${airport.percent}%"></div>`)}
			  <div class="abIataTimespan__time--decoration-down">
				<span>${abLegStops(trip?.waiting?.stops)}</span>
				<span class="abIataTimespan__time--decoration--icon icon icon-circle-question-mark" title="${l('showresult.tooltip.problemWithLeg')}"></span>
			  </div>
			</div>`;
	}

	/* last-segment arrival airport + closing tags + arrival time */
	if (last) {
		html += `
			<div class="abIataTimespan__iata arrive">
			  <span>${segment.arrivalAirport}</span>
			</div>
		  </div> 
		</div>
  
		<div class="abResult__return"><span>${segment.arrivalTime}</span></div>`;
	}

	return html;
}

/**
 * Renders summary information for a journey.
 * @param {Object} journey
 * @param {Object} options
 * @param {boolean} options.multiplePassengers
 * @param {boolean} options.isFavoritePage
 * @param {boolean} options.filters.flexTicket
 * @param {boolean} options.filters.baggageIncluded
 * @param {boolean} options.filters.cabinBaggageIncluded
 * @returns {String} - html
 */
function renderSummaries (journey, options) {
	return `
<div class="abResult__summaries">
	<div class="abResult__segmentInfo">
		<div class="abResult__summary">
		<span class="${ticketTypeIcon(journey.cabin)}"></span>
		<span>${getTicketClassText(journey.cabin, journey)}</span>
	</div>

	${insertIf(options.filters?.flexTicket, () =>
	`<div class="abResult__summary">
		<span class="icon icon-rebooking" style="font-weight:900"></span>
		<span>${l('global.simplePhrases.rebooking')}</span>
	</div>`
	)}

	${insertIf(options.filters?.cabinBaggageIncluded || isFlightAboveEconomy(journey), () =>
	`<div class="abResult__summary">
		<span class="icon icon-checkin-luggage"></span>
		<span>${lreplace('showresult.filter.summary.cabinBaggageIncluded', { included: '' })}</span>
	</div>`
	)}

	${insertIf(options.filters?.baggageIncluded || isFlightAboveEconomy(journey), () =>
	`<div class="abResult__summary">
		<span class="icon icon-cabin-luggage"></span>
		<span>${lreplace('showresult.result.checkedInBagsIncluded', { bags: '' })}</span>
	</div>`
	)}


	${insertIf(options.multiplePassengers, () =>
	`<div class="abResult__summary">
		<span class="icon icon-three-users"></span>
		<span>${lreplace('showresult.result.pricePerPassengers', { passengers: options.multiplePassengers })}</span>
	</div>`
	)}
 
	${insertIf(options.isFavoritePage, () =>
	`<div class="abResult__summary">
		<span class="icon-person-luggage"></span>
		<span>${passengersText(journey)}</span>
	</div>
	<div class="abResult__summary">
		<span class="icon-plane"></span>
		<span>${journey.airlines}</span>
	</div>`
	)}
	</div>
</div>`;
}


/**
 * Computes tracking classes for OTA anchor.
 * @param {number} resultIndex - Result index.
 * @param {Object} agent - Agent object.
 * @param {number} agentIndex - Agent index.
 * @param {string} source - Source string.
 * @param {string} product - Product key.
 * @param {boolean} hasPromotedAgent - Has promoted agent.
 * @param {boolean} [isPromotedAgent=false] - Is promoted agent.
 * @returns {string} - CSS class string.
 */
function getAnchorTrackingClasses (resultIndex, agent, agentIndex, source, product, isPromotedAgent = false) {
	const btnStyles = [
		'otaLink',
		getTrackingClass(`agent-${agent.agent}`),
		getTrackingClass(`result${parseInt(resultIndex) + 1}`),
		getTrackingClass(`ota${parseInt(agentIndex) + 1}`)
	];

	if (!isPromotedAgent && [0, 1].includes(agentIndex)) {
		btnStyles.push(getTrackingClass('otaPrimary'));
	} else if (isPromotedAgent) {
		btnStyles.push(getTrackingClass('otaSecondary'));
	}

	if (isPromotedAgent) {
		btnStyles.push('promotedAgent');
	}

	switch (product) {
		case 'rebookable':
			btnStyles.push(getTrackingClass(`label-flexTicket`));
			break;

		case 'baggage':
			btnStyles.push(getTrackingClass('label-baggageIncluded'));
			break;

		case 'cabinBaggage':
			btnStyles.push(getTrackingClass('label-cabinBaggageIncluded'));
			break;
	}

	// Disable buy button if search date is too old
	if (js_params.searchDateIsTooOld && !(['pricecalendar'].includes(source))) {
		btnStyles.push('otaLink--disabled');
	}

	return btnStyles.join(' ');
}

/**
 * Renders agent preview block.
 * @param {Array} agents - Array of agent objects.
 * @param {number} offset - Offset index.
 * @param {number} numberVisiblePreview - Number of visible agents.
 * @returns {string} - HTML string for agent preview.
 */
function renderResultAgentPreview (agents, offset, numberVisiblePreview) {
	const uniqueAgentNames = [...new Set(agents.map(a => a.agent))];

	const otherAgents = `${l('global.simplePhrases.alsoAt')}: ${
	uniqueAgentNames
		.map(name => capitalize(name))
		.slice(offset + 1, offset + numberVisiblePreview + 1)
		.join(', ')
	}${
	uniqueAgentNames.length > offset + numberVisiblePreview + 1 ? '...' : ''
	}`;

	return `<div class="agentPreview">${otherAgents}</div>`;
}

/**
 * Renders show all agencies block.
 * @param {Array} agents - Array of agent objects.
 * @returns {string} - HTML string for show all agencies.
 */
function renderResultAgentShowAll (agents) {
	return `
<div class="showAllAgencies">
	${lreplace('showresult.result.showAllAgencyCount', { agencyCount: agents.length })}
	<span class="icon-chevron-down">
</div>
	`;
}
